// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app
//   https://api.orioralabs.com/api/apps/solo_focus_log/weekly_summary
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK). Focus-session data stays local; only the scoped
// slice (the past 7 days of sessions) for this one call is sent. We do NOT rebuild the
// brain — the prompt, model routing, and billing all live server-side.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { WeeklySignals } from "./store";
import { webContextFor } from "./search";

const SERVER = "https://api.orioralabs.com";

export type BrainErrorCode = "insufficient_funds" | "managed_ai_blocked" | "unauthenticated" | "unavailable" | "no_store" | "too_large";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/solo_focus_log/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "You're out of funds — add funds to continue.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

// The exact payload the server expects now: a single self-describing { signals } object —
// the derived aggregates computed on-device in Rust (db.rs `WeeklySignals`). We no longer
// send raw per-session rows or reflection text; only counts, rates, histograms, totals, and
// a trend slope leave the Mac. The server brain narrates THESE numbers — observational only.
// See oriora-server/src/routes/catalog-apps/solo-focus-log.ts (operator wires it to match).
export interface WeeklySummaryResult {
  summary: string;
}

export async function weeklySummary(signals: WeeklySignals): Promise<WeeklySummaryResult | null> {
  if (signals.sessions_total === 0) return null;
  const r = await brainCall<Partial<WeeklySummaryResult>>("weekly_summary", { signals });
  const summary = (r.summary ?? "").trim();
  if (!summary) return null;
  return { summary };
}

// ── Goal ideas — DISCOVERY, not advice (see FocusPlanFlow.tsx / GoalsView.tsx for the UI, and
// the server route's own comment for the framing rationale). The client does the actual web
// search ON-DEVICE (Option 3 — search.ts's webContextFor, no Oriora search key) and hands the
// server only the ALREADY-fetched, formatted results to narrate — the server runs no search of
// its own. If the on-device search finds nothing, the server is told plainly rather than left to
// guess, so it says so instead of inventing suggestions.
export interface GoalIdeasResult {
  ideas: string;
}

export async function goalIdeas(goalTitle: string): Promise<GoalIdeasResult | null> {
  const title = goalTitle.trim();
  if (!title) return null;
  const web_context = await webContextFor(`${title} ideas resources community what people do`);
  const r = await brainCall<Partial<GoalIdeasResult>>("goal_ideas", { goal_title: title, web_context });
  const ideas = (r.ideas ?? "").trim();
  if (!ideas) return null;
  return { ideas };
}

// ── Focus Plan drafting (the AI-deepening feature, PROGRAM-DESIGN-style) ───────────
// Bespoke call, NOT the generic brainCall above: draft_focus_plan uses the server's heartbeat
// pattern (whitespace writes while the LLM call is in flight, to survive a proxy's
// time-to-first-byte timeout — the exact bug found + fixed on Workout Tracker's draft_program
// the same day). HTTP status locks to 200 once headers flush, so errors travel as a `_status`
// field in the body instead — this reader checks that field rather than trusting res.status.
export interface DraftPlanSession {
  weekday: number;
  band: "morning" | "afternoon" | "evening" | "night";
  duration_min: number;
  label: string;
  reason: string;
}
export interface DraftFocusPlanResult {
  name: string;
  sessions: DraftPlanSession[];
}

function isValidDraftPlanShape(v: unknown): v is DraftFocusPlanResult {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  if (typeof o.name !== "string" || !Array.isArray(o.sessions)) return false;
  return o.sessions.every((s) => {
    const x = s as Record<string, unknown>;
    return typeof x.weekday === "number" && typeof x.band === "string" &&
      typeof x.duration_min === "number" && typeof x.label === "string" && typeof x.reason === "string";
  });
}

async function draftFocusPlanCall(body: unknown): Promise<Record<string, unknown>> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  let res: Response;
  try {
    res = await fetch(`${SERVER}/api/apps/solo_focus_log/draft_focus_plan`, {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  const text = (await res.text()).trim();
  let parsed: Record<string, unknown>;
  try { parsed = JSON.parse(text); } catch { throw new BrainError("unavailable", "AI returned an unreadable response — please try again."); }
  const status = typeof parsed._status === "number" ? parsed._status : undefined;
  if (status === 402) throw new BrainError("insufficient_funds", "You're out of funds — add funds to continue.");
  if (status === 403) throw new BrainError("managed_ai_blocked", (typeof parsed.message === "string" && parsed.message) || "Connect a vendor key in Oriora settings to use AI for this app.");
  if (status && status >= 500) throw new BrainError("unavailable", "AI is busy — please try again.");
  return parsed;
}

export async function draftFocusPlan(signals: WeeklySignals): Promise<DraftFocusPlanResult> {
  const draft = await draftFocusPlanCall({ signals });
  if (isValidDraftPlanShape(draft)) return draft;
  // One retry on a malformed shape — mirrors Workout Tracker's draft_program client.
  const retry = await draftFocusPlanCall({ signals });
  if (isValidDraftPlanShape(retry)) return retry;
  throw new BrainError("unavailable", "Couldn't draft a valid plan — please try again.");
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/solo_focus_log/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/solo_focus_log/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN cloud
 *  store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora retains
 *  nothing (text relay only). Throws no_store when nothing is connected. Endpoint: the shared
 *  POST /api/apps/solo_focus_log/store route (distinct from the whole-collection `backup` route). */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

// Login defers to the ecosystem web login; session returns via the deep link (App.tsx).
// The web callback already allowlists oriora-monitor + expense-audit-ai; the operator
// adds solo-focus-log server-side. Client side is wired here.
export const openWebLogin = () =>
  invoke<void>("open_external", { url: "https://orioralabs.com/login?return=solo-focus-log" });
